// components/modals/AddExpenseModal.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import SendIcon from '@mui/icons-material/Send';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import Slide from '@mui/material/Slide';
import {ApiDetails, KEY} from "../../../../Constants";
import CryptoJS from "crypto-js";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';


// Modal transition effect
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
}));

const FormContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
}));

// Validation schema
const validationSchema = Yup.object({
    amount: Yup.number()
        .required('Amount is required')
        .positive('Amount must be a positive number')
        .typeError('Please enter a valid number')
        .test(
            'is-decimal',
            'Amount cannot have more than 2 decimal places',
            value => {
                if (!value) return true;
                return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }
        )
        .max(9999999.99, 'Amount must be less than 10 million')
});

const AddExpenseModal = ({
                             open,
                             onClose,
                             expenseDate,
                             setExpenseDate,
                             properties,
                             propertyId,
                             selectedUnit,
                             setSelectedUnit,
                             units,
                             expenseTypes,
                             selectedExpenseType,
                             setSelectedExpenseType,
                             description,
                             setDescription,
                             onPropertySelect,
                             fetchExpenses,
                             landlordId,
                         }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    // New notification state
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    const initialValues = {
        amount: '',
    };
const [originalText, setOriginalText] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});;

    // Handle notification close
    const handleNotificationClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification({...notification, open: false});
    };

    // Show notification helper
    const showNotification = (message, severity) => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Custom validation handler
    const validateForm = (values) => {
        const errors = {};

        // Only validate fields not handled by the Formik validation schema
        if (!description || description.trim() === "") {
            errors.description = "Description is required";
        } else if (description.length < 5) {
            errors.description = "Description must be at least 5 characters";
        } else if (description.length > 500) {
            errors.description = "Description must be less than 500 characters";
        }

        if (!propertyId) {
            errors.propertyId = "Please select a property";
        }

        if (!selectedExpenseType) {
            errors.expenseType = "Please select an expense type";
        }

        if (!expenseDate) {
            errors.expenseDate = "Date is required";
        } else if (expenseDate > new Date()) {
            errors.expenseDate = "Date cannot be in the future";
        }

        return errors;
    };

    // Add expense function implemented directly in the component
    const addExpense = (values) => {
        // Perform custom validation
        const errors = validateForm(values);

        // If there are validation errors, show the first one and don't proceed
        if (Object.keys(errors).length > 0) {
            const firstError = Object.values(errors)[0];
            showNotification(firstError, 'error');
            return;
        }

        setIsSubmitting(true);

        const body = {
            description: description,
            property_id: propertyId,
            expense_type: selectedExpenseType,
            house_number: selectedUnit || "",
            cost: values.amount,
            landlord_id: landlordId,
            year: expenseDate.getFullYear(),
            month: expenseDate.getMonth(),
            day: expenseDate.getDate(),
        };

        fetch(ApiDetails + "pegasus/visionary/expenses/add", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                setIsSubmitting(false);

                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    showNotification("Expense Added Successfully", 'success');

                    // Reset form fields
                    setSelectedUnit(null);
                    setDescription("");

                    // Fetch updated expenses and close modal
                    fetchExpenses();

                    // Delay closing to allow user to see success message
                    setTimeout(() => {
                        onClose();
                    }, 1500);
                } else {
                    showNotification("Failed to add expense", 'error');
                }
            })
            .catch((err) => {
                console.log(err);
                setIsSubmitting(false);
                showNotification("Something Went Wrong. Call Customer Care For Assistance.", 'error');
            });
    };

    return (
        <>
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                maxWidth="md"
                fullWidth
                onClose={onClose}
                aria-describedby="add-expense-dialog"
            >
                <StyledDialogTitle>
                    <Typography variant="h6" align="center">Add New Expense</Typography>
                </StyledDialogTitle>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={addExpense}
                >
                    {({ touched, errors, values }) => (
                        <Form>
                            <DialogContent dividers>
                                <FormContainer>
                                    <Grid container spacing={3}>
                                        {/* Date */}
                                        <Grid item xs={12} md={6}>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    disableFuture
                                                    label="Expense Date"
                                                    openTo="day"
                                                    views={['year', 'month', 'day']}
                                                    value={expenseDate}
                                                    onChange={(newValue) => {
                                                        setExpenseDate(newValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            fullWidth
                                                            variant="outlined"
                                                            {...params}
                                                            error={false}
                                                        />
                                                    )}
                                                />
                                            </LocalizationProvider>
                                        </Grid>

                                        {/* Amount */}
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                as={TextField}
                                                name="amount"
                                                label="Amount (KSH)"
                                                type="number"
                                                fullWidth
                                                required
                                                variant="outlined"
                                                error={touched.amount && Boolean(errors.amount)}
                                                helperText={touched.amount && errors.amount}
                                            />
                                        </Grid>

                                        {/* Property */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl
                                                required
                                                fullWidth
                                                error={!propertyId}
                                            >
                                                <InputLabel>Property</InputLabel>
                                                <Select
                                                    value={propertyId || ''}
                                                    label="Property *"
                                                    onChange={(e) => onPropertySelect(e.target.value)}
                                                >
                                                    {properties.map((property) => (
                                                        <MenuItem key={property.id} value={property.id}>
                                                            {property.property_name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {!propertyId && (
                                                    <FormHelperText>Please select a property</FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>

                                        {/* Unit */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl
                                                fullWidth
                                                disabled={!units}
                                            >
                                                <InputLabel>House Number (Optional)</InputLabel>
                                                <Select
                                                    value={selectedUnit || ''}
                                                    label="House Number (Optional)"
                                                    onChange={(e) => setSelectedUnit(e.target.value)}
                                                >
                                                    {units?.map((unit) => (
                                                        <MenuItem key={unit.unit} value={unit.unit}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                                <Typography>{unit.unit}</Typography>
                                                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                                    {unit.state}
                                                                </Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>
                                                    {!units ? 'Select a property first' : 'Select a unit (optional)'}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>

                                        {/* Expense Type */}
                                        <Grid item xs={12}>
                                            <FormControl
                                                required
                                                fullWidth
                                                error={!selectedExpenseType}
                                            >
                                                <InputLabel>Expense Type</InputLabel>
                                                <Select
                                                    value={selectedExpenseType || ''}
                                                    label="Expense Type *"
                                                    onChange={(e) => setSelectedExpenseType(e.target.value)}
                                                >
                                                    {expenseTypes.map((type) => (
                                                        <MenuItem key={type.id} value={type.id}>
                                                            {type.type}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {!selectedExpenseType && (
                                                    <FormHelperText>Please select an expense type</FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid>

                                        {/* Description */}
                                        <Grid item xs={12}>
                                            <SectionTitle variant="subtitle1">Expense Description</SectionTitle>
                                            <Divider sx={{ mb: 2 }} />
                                            <TextField
                                                label="Description"
                                                multiline
                                                rows={4}
                                                value={description || ''}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder="Enter a detailed description of the expense..."
                                                variant="outlined"
                                                fullWidth
                                                required
                                                error={!description}
                                                helperText={!description ? 'Description is required' : ''}
                                            />
                                        </Grid>
                                    </Grid>
                                </FormContainer>
                            </DialogContent>

                            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    color="success"
                                    type="submit"
                                    startIcon={<SendIcon />}
                                    loadingPosition="start"
                                    loading={isSubmitting}
                                >
                                    Add Expense
                                </LoadingButton>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {/* New improved notification component */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleNotificationClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleNotificationClose}
                    severity={notification.severity}
                    variant="filled"
                    elevation={6}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

AddExpenseModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    expenseDate: PropTypes.object.isRequired,
    setExpenseDate: PropTypes.func.isRequired,
    properties: PropTypes.array.isRequired,
    propertyId: PropTypes.string,
    selectedUnit: PropTypes.string,
    setSelectedUnit: PropTypes.func.isRequired,
    units: PropTypes.array,
    expenseTypes: PropTypes.array.isRequired,
    selectedExpenseType: PropTypes.string,
    setSelectedExpenseType: PropTypes.func.isRequired,
    description: PropTypes.string,
    setDescription: PropTypes.func.isRequired,
    onPropertySelect: PropTypes.func.isRequired,
    fetchExpenses: PropTypes.func.isRequired,
    landlordId: PropTypes.string.isRequired,
};

AddExpenseModal.defaultProps = {
    propertyId: '',
    selectedUnit: '',
    selectedExpenseType: '',
    description: '',
};

export default AddExpenseModal;